import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import reportslogo from "../../assets/images/reportslogo.png";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "../commoncomponents/print.css"; // Import the print CSS
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { defaultLightTheme } from "../../assets/styled-components/themes/themelist";
import { useSelector } from "react-redux";
import "jspdf-autotable";

const PpmCalendarPdf = forwardRef(
  (
    {
      onRenderComplete,
      year,
      workgroup,
      proposedby,
      approvedby,
      headers=[],
      data=[]
    },
    ref
  ) => {
    console.log(headers,data);
    
    const printRef = useRef(null);
    const { color } = useSelector((state) => state.theme);


    useEffect(() => {
      if (onRenderComplete) {
        onRenderComplete();
      }
    }, [onRenderComplete]);

    const handleDownloadPDF = async () => {
      const input = printRef.current;
      if (!input) return;

      // Wait until the element is fully rendered
      await new Promise((resolve) => setTimeout(resolve, 100)); // 100ms delay

      // Create a new jsPDF instance
      const pdf = new jsPDF("p", "pt", "a4");
      const padding = 20; // Define padding around content

      // Capture the content with html2canvas
      const canvas = await html2canvas(input, {
        scale: 2, // Ensures high quality
        useCORS: true, // Handles cross-origin issues
      });

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 595.28 - padding * 2; // A4 width in points minus padding
      const pageHeight = 841.89; // A4 height in points
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = padding;

      // Add image to the first page
      pdf.addImage(imgData, "PNG", padding, position, imgWidth, imgHeight);
      heightLeft -= pageHeight - padding * 2;

      // Handle multiple pages if content height exceeds one page
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + padding;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", padding, position, imgWidth, imgHeight);
        heightLeft -= pageHeight - padding * 2;
      }

      // Save the PDF
      pdf.save(`PPM Calendar Report.pdf`);
    };

    const monthWeeksCount = {};
    const header = headers
    // Loop through the data and count weeks for each month
    header.forEach(item => {
      if (item.header && item.subHeader.endsWith('W')) {
        if (!monthWeeksCount[item.header]) {
          monthWeeksCount[item.header] = 0;
        }
        monthWeeksCount[item.header]++;
      }
    });

    // Map the results to the desired format
    const months = Object.keys(monthWeeksCount).map(month => ({
      month,
      weeks: monthWeeksCount[month]
    }));

    // Sample data for the assets
    const sampleData = data
    useImperativeHandle(ref, () => ({
      handleDownloadPDF,
    }));

    return (
      <MuiThemeProvider
        theme={{
          ...defaultLightTheme,
          palette: {
            ...defaultLightTheme.palette,
            background: {
              default: color !== "default" && "rgba(17, 17, 17, 1)",
            },
          },
        }}
      >
        <CssBaseline />

        <Box ref={printRef} className="print-content">
          <Box className="table-section">
            <Box textAlign="center">
              <img src={reportslogo} alt="logo" width={"8%"} />
            </Box>
            <Box textAlign="center">
              <Typography variant="title1">
                Planned Preventive Maintenance Calendar (PPM)
              </Typography>
            </Box>
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Box>
                <Typography variant="subtitle2">
                  YEAR : {year}
                </Typography>
                <Typography variant="subtitle2">
                  WORK GROUP : {workgroup}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2">
                  PROPOSED BY : {proposedby}

                </Typography>
                <Typography variant="subtitle2">
                  APPROVED BY : {approvedby}
                </Typography>
              </Box>

            </Stack>
            <TableContainer
              component={Paper}
              sx={{ width: sampleData.length === 0 ? '1300px' : 'auto' }} // Adjust width based on data length
            >
              <Table size="small">
                {/* Table Head */}
                <TableHead sx={{ backgroundColor: "#B995D5" }}>
                  {/* First Header Row (Asset No, Asset Description, Months) */}
                  <TableRow>
                    <TableCell rowSpan={2} align="center" sx={{ fontWeight: 'bold', padding: '2px', fontSize: '0.7rem', color: 'white' }}>Asset No</TableCell>
                    <TableCell rowSpan={2} align="center" sx={{ fontWeight: 'bold', padding: '2px', fontSize: '0.7rem', color: 'white' }}>Asset Description</TableCell>
                    {months.map((month, index) => (
                      <TableCell
                        key={index}
                        align="center"
                        colSpan={month.weeks}
                        sx={{ fontWeight: 'bold', backgroundColor: '#B995D5', padding: '2px', fontSize: '0.7rem', color: 'white' }}
                      >
                        {month.month}
                      </TableCell>
                    ))}
                  </TableRow>
                  {/* Week Header (W1, W2, W3, ...) */}
                  <TableRow>
                    {months.map((month) =>
                      Array.from({ length: month.weeks }).map((_, weekIndex) => (
                        <TableCell key={weekIndex} align="center" sx={{ fontWeight: 'bold', padding: '2px', fontSize: '0.7rem', color: 'white' }}>
                          {`W${weekIndex + 1}`}
                        </TableCell>
                      ))
                    )}
                  </TableRow>
                </TableHead>

                {/* Table Body */}
                <TableBody>
                  {sampleData.length > 0 ? (
                    sampleData.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        <TableCell align="center" sx={{ padding: '2px', fontSize: '0.7rem' }}>{row.assetno}</TableCell>
                        <TableCell sx={{ padding: '2px', fontSize: '0.7rem' }}>{row.assetdesc}</TableCell>
                        {months.map((month) =>
                          Array.from({ length: month.weeks }).map((_, weekIndex) => {
                            // Construct week key dynamically like `jan_week2`, `feb_week5`
                            const weekKey = `${month.month.split('-')[0].toLowerCase()}_week${weekIndex + 1}`;
                            return (
                              <TableCell key={weekIndex} sx={{
                                padding: '2px',
                                width: '40px', // Adjust width as needed
                                height: '100px', // Increase height if needed for better spacing
                                textAlign: 'center',
                                position: 'relative', // Allows better control over positioned elements
                               }}>
                                <Typography sx={{
                                 fontSize: '0.7rem',
                                 whiteSpace: 'nowrap', // Prevents line break
                                 WebkitTransform: 'rotate(90deg)',
                                 position: 'absolute', // Allows precise positioning within the cell
                                 top: '50%', // Center vertically in cell
                                 left: '50%', // Center horizontally in cell
                                 translate: '-50% -50%', // Fine-tunes the centering
                                }}>{row[weekKey] || ''}</Typography>
                              </TableCell>
                            );
                          })
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={headers.length + 2} align="center" sx={{ padding: '10px', fontSize: '1rem', color: '#B995D5' }}>
                        No Records Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </MuiThemeProvider>
    );
  }
);

export default PpmCalendarPdf;
